import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Box,
  IconButton,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import Key from '../../clientVariables.json';
import { useContactContext } from '../../contexts/ContactContext';

const AddContactModal = ({ isOpen, onClose }) => {
  const { availableTags, addNewTags, fetchContacts } = useContactContext();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [tags, setTags] = useState([]);
  const [errors, setErrors] = useState({});

  // State and city management
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);

  // Fetch US states on modal open
  useEffect(() => {
    if (isOpen) {
      fetchStates();
    }
  }, [isOpen]);

  // Function to fetch US states (country_id 233 is for US)
  const fetchStates = () => {
    setIsLoadingStates(true);
    axios
      .post(`${Key.domain_jnp}/commonAPIs/get_states_by_countryid`, {
        country_id: 233 // US Country ID
      })
      .then((response) => {
        console.log("States API response:", response.data.data);

        // Map states from API response
        const stateOptions = response.data.data.map((state) => ({
          value: state.id,
          label: state.name
        }));

        setStates(stateOptions);
        setIsLoadingStates(false);
      })
      .catch(error => {
        console.error("Error fetching states:", error);
        setIsLoadingStates(false);
      });
  };

  // Function to fetch cities for a selected state
  const fetchCities = (stateId) => {
    if (!stateId) {
      setCities([]);
      return;
    }

    setIsLoadingCities(true);
    axios
      .post(`${Key.domain_jnp}/commonAPIs/get_cities_by_stateid`, {
        state_id: stateId
      })
      .then((response) => {
        console.log("City API response:", response.data.data);

        // Map cities from API response
        const cityOptions = response.data.data.map((city) => ({
          value: city.id,
          label: city.cityName
        }));

        setCities(cityOptions);
        setIsLoadingCities(false);
      })
      .catch(error => {
        console.error("Error fetching cities:", error);
        setIsLoadingCities(false);
        setCities([]);
      });
  };

  const handleStateChange = (event, newValue) => {
    setSelectedState(newValue);
    setSelectedCity(null);

    // Find the state name from the id for sending in search
    const selectedState = states.find(state => state.value === newValue?.value);
    // setSelectedStateName(selectedState ? selectedState.label : '');

    // Fetch cities for the selected state
    if (newValue?.value) {
      fetchCities(newValue.value);
    } else {
      setCities([]);
    }
  };

  const handleCityChange = (event, newValue) => {
    setSelectedCity(newValue);
    // Find the city name from the id for sending in search
    // const selectedCity = cities.find(city => city.value === cityId);
    // setSelectedCityName(selectedCity ? selectedCity.label : '');
  }; const validateLinkedInUrl = (url) => {
    if (!url) return true; // Optional field, so empty is valid
    const linkedinRegex = /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[\w-]{5,30}[/]?$/i;
    return linkedinRegex.test(url);
  };
  const validateForm = () => {
    const newErrors = {};
    // Only email is required
    if (!email.trim()) newErrors.email = 'Email is required';
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      newErrors.email = 'Email is invalid';
    }
    // Validate LinkedIn URL if provided
    if (linkedinUrl && !validateLinkedInUrl(linkedinUrl)) {
      newErrors.linkedinUrl = 'Please enter a valid LinkedIn URL (e.g., https://linkedin.com/in/username)';
    }
    // All other fields are optional

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }
    axios.post(`${Key.domain}/common/add-contactCRM`, {
      name: name || '', // Send empty string if null
      email, // Email is required
      phone_number: phoneNumber || '', // Backend expects phone_number
      job_title: jobTitle || '', // Backend expects job_title
      state_name: selectedState?.label || '', // Backend expects state_name
      city_name: selectedCity?.label || '', // Backend expects city_name, optional now
      state_id: selectedState?.value || null, // Backend expects state_id
      city_id: selectedCity?.value || null, // Backend expects city_id, optional now
      linkedin_url: linkedinUrl || '', // Backend expects linkedin_url
      tags: tags.join(','),
      }).then(async (response) => {
      console.log("Contact added:", response.data);

      if (response.data.success === 1) {
        try {
          // Refresh contacts from API instead of manually adding to context
          await fetchContacts();
          
          // Reset form and close modal
          setName('');
          setEmail('');
          setJobTitle('');
          setPhoneNumber('');
          setLinkedinUrl('');
          setSelectedState(null);
          setSelectedCity(null);
          setCities([]);
          setTags([]);
          setErrors({});
          
          // Call onClose to close the modal
          onClose();
        } catch (error) {
          console.error("Error refreshing contacts:", error);
          // You could add a toast notification here
          alert("Contact added successfully, but there was an issue refreshing the contacts list.");
        }
      } else {
        // API returned success:0
        console.error("API returned error:", response.data);
        alert("There was an error adding the contact. Please check the form and try again.");
      }
    })
    .catch(error => {
      console.error("Error adding contact:", error);
      
      // Handle specific error cases
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        alert(`Server error: ${error.response.data.message || "Unknown error"}`);
      } else if (error.request) {
        // The request was made but no response was received
        alert("No response from server. Please check your internet connection and try again.");
      } else {
        // Something happened in setting up the request that triggered an Error
        alert(`Error: ${error.message || "Unknown error occurred"}`);
      }
    });
  };
  const handleClose = () => {
    // Reset form on close
    setName('');
    setEmail('');
    setJobTitle('');
    setPhoneNumber('');
    setLinkedinUrl('');
    setSelectedState(null);
    setSelectedCity(null);
    setCities([]);
    setTags([]);
    setErrors({});

    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        Add New Contact
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Grid>          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Job Title"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              error={!!errors.jobTitle}
              helperText={errors.jobTitle}
            />
          </Grid><Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    +1
                  </InputAdornment>
                ),
              }}
              // Not required
            />
          </Grid>          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="LinkedIn URL"
              value={linkedinUrl}
              onChange={(e) => setLinkedinUrl(e.target.value)}
              placeholder="https://linkedin.com/in/username"
              error={!!errors.linkedinUrl}
              helperText={errors.linkedinUrl || "LinkedIn profile URL (optional)"}
              // Not required
            />
          </Grid>
          <Grid item xs={12} sm={6}>            <Autocomplete
              value={selectedState}
              onChange={handleStateChange}
              options={states}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              disableClearable={!!selectedState}
              loading={isLoadingStates}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="State"
                  error={!!errors.state}
                  helperText={errors.state}
                  InputLabelProps={{
                    shrink: true
                  }} InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingStates ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>          <Grid item xs={12} sm={6}>
            <Autocomplete
              value={selectedCity}
              onChange={handleCityChange}
              options={cities}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              disableClearable={!!selectedCity}
              loading={isLoadingCities}
              disabled={!selectedState || isLoadingCities}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth label="City"
                  // City is not required anymore
                  error={!!errors.city}
                  helperText={errors.city}
                  InputLabelProps={{
                    shrink: true
                  }} InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingCities ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>            <Autocomplete
            multiple
            options={availableTags}
            value={tags}
            onChange={(event, newValue) => {
              setTags(newValue);
              // Add any new tags to the context
              const newCustomTags = newValue.filter(tag => !availableTags.includes(tag));
              if (newCustomTags.length > 0) {
                addNewTags(newCustomTags);
              }
            }}
            freeSolo
            filterSelectedOptions
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Tags"
                placeholder="Add tags (type and press Enter)"
                helperText="Type a new tag and press Enter to add it"
              />
            )}
          />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Add Contact
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddContactModal;
